import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';

import { LiveBroadcastIcon } from '@/components/hub/HubIcons';
import { PanelNotice, PanelWaiting } from '@/components/hub/PanelNotice';
import { useHaptics } from '@/hooks/useHaptics';
import { useQgammonLive } from '@/hooks/useQgammonLive';
import { useStartMatch } from '@/hooks/useStartMatch';
import { minutiDa, orologioDi, type RigaLive } from '@/lib/qgammon/partiteLive';
import { cn } from '@/lib/cn';
import { useAppDispatch } from '@/store/hooks';
import { setHubSection } from '@/store/core.slice';
import type { QgammonLiveSlot } from '@/types/qgammon';

/**
 * Hub backgammon · scheda «Live» — le partite che si stanno giocando adesso (Luca I-208, 26/09).
 *
 * Impianto APPROVATO: variante (a) «lista compatta», L-2298 (Luca, 26/09 12:54:52Z) — KB
 * `services/qlash-telegram-app/ui/2026-09-26-backgammon-hub-live-varianti.md`. Una card per partita
 * su due righe, punteggio a destra; la MIA partita una volta sola, in testa, con «Riprendi»; lista
 * vuota = variante (d). ⛔ Le parole NON vengono dall'immagine: niente durate («blitz»), L-722.
 *
 * Dati e casi: `useQgammonLive` (`GET /matches/live` del gameserver, polling 15 s, i cinque casi).
 * Montato in due posti dall'elenco unico `lobbySections.ts` (Hub e `/backgammon`).
 */
export function QgammonLivePanel() {
  const { t } = useTranslation('backgammon');
  const live = useQgammonLive();
  const vaiAGioca = useVaiAGioca();

  if (live.verdict === 'loading' || live.verdict === 'slow') {
    return <PanelWaiting slow={live.verdict === 'slow'} />;
  }
  if (live.verdict === 'error' || live.verdict === 'unavailable') {
    return (
      <div className="flex min-h-0 flex-1 flex-col py-2">
        <PanelNotice tone={live.verdict} onRetry={live.riprova} retrying={live.inVolo} />
      </div>
    );
  }
  if (live.verdict === 'empty') {
    return (
      <div
        data-testid="bg-live-vuota"
        className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-2xl bg-bg-secondary px-6 py-8 text-center"
      >
        <LiveBroadcastIcon aria-hidden className="h-12 w-12 text-brand" />
        <p className="text-base font-bold text-fg">
          {t('live.emptyTitle', { defaultValue: 'No matches in progress right now' })}
        </p>
        <p className="max-w-[18rem] text-sm text-fg-muted">
          {t('live.emptyHint', { defaultValue: 'When someone is playing, the match shows up here.' })}
        </p>
        <button
          type="button"
          onClick={vaiAGioca}
          className="mt-1 rounded-full border border-brand px-8 py-2 text-sm font-semibold text-brand transition-opacity active:opacity-70"
        >
          {t('live.play', { defaultValue: 'Play' })}
        </button>
      </div>
    );
  }

  const ora = Date.now();
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto py-2">
      <p className="px-1 text-sm font-semibold text-fg-muted">
        {t('live.header', { defaultValue: 'In progress now' })}{' '}
        <span className="text-fg">{live.righe.length}</span>
      </p>
      {live.nonAggiornato && (
        <p data-testid="bg-live-non-aggiornato" className="px-1 text-xs font-semibold text-warning">
          {t('live.stale', { defaultValue: 'Not updated: the service is not answering' })}
        </p>
      )}
      {live.righe.map((r) =>
        r.mioColore !== null ? (
          <CardMia key={r.match.matchId} riga={r} ora={ora} />
        ) : (
          <CardPartita key={r.match.matchId} riga={r} ora={ora} />
        ),
      )}
    </div>
  );
}

/** La mia partita: bordata, in testa, con «Riprendi» (si riprende, non si guarda). */
function CardMia({ riga, ora }: { riga: RigaLive; ora: number }) {
  const { t } = useTranslation('backgammon');
  const haptics = useHaptics();
  const startMatch = useStartMatch();
  const { match } = riga;
  return (
    <div data-testid="bg-live-mia" className="rounded-2xl border border-brand bg-bg-secondary p-3">
      <p className="text-xs font-semibold text-fg-muted">
        {t('live.mine', { defaultValue: 'Your match' })}
      </p>
      <div className="mt-1 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <Giocatori riga={riga} />
          <Meta riga={riga} ora={ora} />
        </div>
        <Punteggio riga={riga} />
        <button
          type="button"
          onClick={() => {
            haptics.impact('light');
            startMatch({
              game: 'qgammon',
              matchId: match.matchId,
              perspective: riga.mioColore ?? undefined,
              // `/matches/live` esclude gli appuntamenti: quella che vedo qui si sta GIOCANDO.
              status: 'active',
            });
          }}
          className="shrink-0 rounded-full bg-brand px-4 py-2 text-sm font-bold text-bg transition-opacity active:opacity-80"
        >
          {t('resume.live.cta', { defaultValue: 'Resume' })}
        </button>
      </div>
    </div>
  );
}

/** La partita di altri: un tocco la apre in SOLA LETTURA (`/backgammon/live/:id`, spettatore anonimo). */
function CardPartita({ riga, ora }: { riga: RigaLive; ora: number }) {
  const navigate = useNavigate();
  const haptics = useHaptics();
  return (
    <button
      type="button"
      data-testid="bg-live-riga"
      onClick={() => {
        haptics.selection();
        navigate(`/backgammon/live/${encodeURIComponent(riga.match.matchId)}`);
      }}
      className="flex w-full items-center gap-3 rounded-2xl bg-bg-secondary p-3 text-left transition-opacity active:opacity-80"
    >
      <div className="min-w-0 flex-1">
        <Giocatori riga={riga} />
        <Meta riga={riga} ora={ora} />
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <Punteggio riga={riga} />
        <Spettatori n={riga.match.spectators} />
      </div>
    </button>
  );
}

function Giocatori({ riga }: { riga: RigaLive }) {
  const { t } = useTranslation('backgammon');
  const { match } = riga;
  return (
    <p className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-sm text-fg">
      <Lato slot={match.white} mio={riga.mioColore === 'W'} />
      <span className="text-fg-muted">{t('live.vs', { defaultValue: 'vs' })}</span>
      <Lato slot={match.black} mio={riga.mioColore === 'B'} />
    </p>
  );
}

function Lato({ slot, mio }: { slot: QgammonLiveSlot; mio: boolean }) {
  const { t } = useTranslation('backgammon');
  return (
    <span className="inline-flex min-w-0 items-baseline gap-1">
      {!slot.connected && (
        <span
          role="img"
          aria-label={t('live.disconnected', { defaultValue: 'not connected' })}
          className="inline-block h-1.5 w-1.5 shrink-0 self-center rounded-full bg-fg-muted"
        />
      )}
      <span className={cn('truncate font-semibold', mio && 'text-brand')}>
        {slot.displayName}
      </span>
      {slot.elo !== null && <span className="text-xs text-fg-muted">{slot.elo}</span>}
      {slot.isBot && (
        <span className="self-center rounded bg-bg-tertiary px-1 py-0.5 text-[10px] font-bold uppercase tracking-wide text-fg-muted">
          {t('hubPanels.bot', { defaultValue: 'Bot' })}
        </span>
      )}
    </span>
  );
}

function Punteggio({ riga }: { riga: RigaLive }) {
  const { match } = riga;
  return (
    <span data-testid="bg-live-punteggio" className="shrink-0 text-lg font-bold tabular-nums text-fg">
      {match.scoreWhite} - {match.scoreBlack}
    </span>
  );
}

/** «al 5 · con orologio · da 12 min» — nessuna durata (L-722), un pezzo che non si sa non si scrive. */
function Meta({ riga, ora }: { riga: RigaLive; ora: number }) {
  const { t } = useTranslation('backgammon');
  const { match } = riga;
  const pezzi: string[] = [];
  pezzi.push(
    match.matchLength > 1
      ? t('live.matchTo', { n: match.matchLength, defaultValue: 'to {{n}}' })
      : t('live.single', { defaultValue: 'single game' }),
  );
  const orologio = orologioDi(match);
  if (orologio !== null) {
    pezzi.push(
      orologio === 'unlimited'
        ? t('live.noClock', { defaultValue: 'no clock' })
        : t('live.clock', { defaultValue: 'with clock' }),
    );
  }
  const min = minutiDa(match.startedAt, ora);
  if (min !== null) {
    pezzi.push(
      min === 0
        ? t('live.justStarted', { defaultValue: 'just started' })
        : t('live.since', { n: min, defaultValue: '{{n}} min ago' }),
    );
  }
  return (
    <p data-testid="bg-live-meta" className="mt-0.5 truncate text-xs text-fg-muted">
      {pezzi.join(' · ')}
    </p>
  );
}

function Spettatori({ n }: { n: number }) {
  const { t } = useTranslation('backgammon');
  if (!(n > 0)) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-fg-muted"
      aria-label={t('live.spectators', { count: n, defaultValue: '{{count}} watching' })}
    >
      <Eye aria-hidden className="h-3.5 w-3.5" />
      {n}
    </span>
  );
}

/**
 * «Gioca» della lista vuota: la prima scheda dell'elenco. Il pannello e' montato in DUE posti e
 * ciascuno tiene la scheda attiva a modo suo — `/backgammon` in `?tab=` (vuoto = la prima), la Hub
 * nello store (`hubSection`).
 * 🔴 Dal 01/10 la prima scheda e' `'ranked'` con l'etichetta «Gioca» (Luca, I-282): la scheda `'play'`
 * di prima non esiste piu'. Prima si scriveva `'play'` nello store, e la Hub ripiegava sulla prima scheda
 * solo perche' la chiave non c'era piu' — giusto per caso. Ora la chiave e' quella vera.
 */
function useVaiAGioca() {
  const haptics = useHaptics();
  const { pathname } = useLocation();
  const [, setParams] = useSearchParams();
  const dispatch = useAppDispatch();
  return () => {
    haptics.selection();
    if (pathname.startsWith('/backgammon')) setParams({}, { replace: true });
    else dispatch(setHubSection('ranked'));
  };
}
